import DefaultLayout from "@/layouts/DefaultLayout";
import { NextPage } from "next";
import { NextSeo } from "next-seo";
import useSearchQueryParameter from "@/hooks/useSearchQueryParameter";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import useExistCategories from "@/hooks/useExistCategories";
import Link from "next/link";
import ProductVariationItemList from "@/features/product_variation/components/ProductVariationItemList";
import {
  ColorType,
  ProductKind,
  ProductVariationWithProductResponse,
} from "@/web-client/api";
import CategoryHeader from "@/components/CategoryHeader";
import CategoryContainer, {
  SearchBtnParam,
} from "@/components/CategoryContainer";
import useLatestProductVariations from "@/features/product_variation/hooks/useLatestProductVariations";
import publicPath from "@/utils/publicPath";
import CategoryItemContainer from "@/components/CategoryItemContainer";
import SectionContainer from "@/components/SectionContainer";
import CategoryItem from "@/components/CategoryItem";
import SearchLink from "@/components/SearchLink";
import MoreBtnLink from "@/components/MoreBtnLink";
import usePageView from "@/hooks/usePageView";
import ProductVariationItem from "@/features/product_variation/components/ProductVariationItem";
import useArrayMutation from "@/hooks/useArrayMutation";

const SearchBtnParams: SearchBtnParam<ProductSearchQueryParameter>[] = [
  {
    label: "「ダイニングチェア」 × 「ブルー」",
    query: {
      colors: ColorType.BLUE.toString(),
      category_id: "2",
    },
  },
  {
    label: "「ダイニングテーブル」 × 「木材」 × 「幅1,800mm以上」",
    query: {
      category_id: "19",
      material_id: "1",
      min_width: "1800",
    },
  },
  {
    label: "「オフィスチェア」 × 「ブラック」",
    query: {
      category_id: "4",
      colors: ColorType.BLACK.toString(),
    },
  },
  {
    label: "「チェア」 × 「木材」 × 「ブラック」",
    query: {
      material_id: "1",
      category_id: "1",
      colors: ColorType.BLACK.toString(),
    },
  },
];

const description =
  "TECTUREに登録された家具をご紹介します。国内外のデザイナーが意匠を凝らしたさまざまなデザイン・スタイルの家具を登録しています。色やサイズ・素材など様々な軸からTECTURE内に登録された家具を検索することが可能です。";

const InteriorsIndexPage: NextPage = () => {
  usePageView();
  const { queryController } =
    useSearchQueryParameter<ProductSearchQueryParameter>(
      "/interiors/product_variations/search",
    );
  const { existInteriorCategories } = useExistCategories();
  const { productVariations, mutate } = useLatestProductVariations(
    "/latest_interiors",
    12,
    ProductKind.INTERIOR,
  );
  const { update } = useArrayMutation<ProductVariationWithProductResponse>();

  return (
    <DefaultLayout hiddenFooter={false}>
      <NextSeo
        title={`家具をさがす`}
        description={description}
        canonical={publicPath("/interiors")}
      />

      <CategoryContainer
        header={
          <CategoryHeader
            title={"家具を探す"}
            description={description}
            backgroundImageUrl="/imgs/bg_interior.jpg"
            linkElm={
              <small>
                建材を探す場合は
                <Link
                  href="/building_materials"
                  className="underline font-bold"
                >
                  こちら
                </Link>
              </small>
            }
            btns={
              <>
                <Link
                  href="/interiors/product_variations/search"
                  className="flex justify-between items-center gap-12 text-primary bg-white rounded-full px-16 py-12 text-center text-sm font-bold"
                >
                  <span>すべての家具</span>
                  <i className="icon icon-Arrow-right text-xl"></i>
                </Link>

                <Link
                  href="/interiors/product_variations/search?in_stock=1"
                  className="flex justify-between items-center gap-12 bg-yellow-500 rounded-full px-16 py-12 text-center font-bold text-sm"
                >
                  <span>サンプル請求できる家具</span>
                  <i className="icon icon-Arrow-right text-xl"></i>
                </Link>
              </>
            }
          />
        }
      >
        <SectionContainer title="カテゴリから探す" hiddenUnderline={true}>
          <CategoryItemContainer>
            {existInteriorCategories.map((category) => (
              <li key={category.id}>
                <CategoryItem
                  name={category.name}
                  imgUrl={`/imgs/category/${category.id}_thumb.jpg`}
                  href={queryController.toPath({
                    category_id: category.id.toString(),
                  })}
                />
              </li>
            ))}
          </CategoryItemContainer>
        </SectionContainer>

        <SectionContainer title="おすすめの検索" hiddenUnderline={true}>
          <ul className="Cloud small:px-8">
            {SearchBtnParams.map((param) => (
              <li key={param.label}>
                <SearchLink
                  href={queryController.toPath(param.query)}
                  label={param.label}
                />
              </li>
            ))}
          </ul>
        </SectionContainer>

        <SectionContainer
          title="家具一覧"
          hiddenUnderline={true}
          moreLink={"/interiors/product_variations/search"}
        >
          <div className="flex flex-col space-y-32">
            <div className="px-8">
              <ProductVariationItemList
                productVariations={productVariations}
                onRender={(variation) => (
                  <ProductVariationItem
                    productVariation={variation}
                    mutate={(updated) =>
                      mutate(update(productVariations, updated), false)
                    }
                  />
                )}
              />
            </div>
            <div className="mx-auto">
              <MoreBtnLink href="/interiors/product_variations/search" />
            </div>
          </div>
        </SectionContainer>
      </CategoryContainer>
    </DefaultLayout>
  );
};

export default InteriorsIndexPage;
